import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { serviceIcon, serviceLabel, STATUS_LABEL, formatZAR } from "@/lib/constants";
import { ArrowLeft, Phone, MessageCircle, MapPin, Star, CheckCircle, CreditCard, Share2, Banknote, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["pending", "accepted", "in_progress", "completed"];

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState(null); // 'card' | 'eft' | 'cash' | null

  const load = async () => {
    const { data } = await api.get(`/requests/${id}`);
    setR(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!r) return <div className="p-6 text-sm text-[#9CA3AF]">Loading...</div>;

  const isClient = user.role === "client" && user.id === r.userId;
  const isAssigned = user.role === "mechanic" && r.assignedMechanicId === user.id;
  const canAccept = user.role === "mechanic" && r.status === "pending";

  const accept = async () => {
    try {
      await api.post(`/requests/${id}/accept`);
      toast.success("Job accepted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.post(`/requests/${id}/status`, { status });
      toast.success(`Status: ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitRating = async () => {
    try {
      await api.post("/ratings", { requestId: id, rating, comment });
      toast.success("Rating submitted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        jobId: id,
        originUrl: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setPaying(false);
    }
  };

  const markPaid = async (method) => {
    setPaying(true);
    try {
      await api.post(`/requests/${id}/mark-paid`, { method });
      toast.success(method === "cash" ? "Marked as paid (cash)" : "Payment recorded");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPaying(false);
    }
  };

  const currentIdx = STATUS_ORDER.indexOf(r.status);

  return (
    <div>
      <div className="px-6 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="detail-back-btn"><ArrowLeft /></button>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Request Detail</span>
      </div>

      <div className="px-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-4xl mb-2">{serviceIcon(r.serviceType)}</div>
            <h1 className="font-display text-2xl font-black tracking-tight">{serviceLabel(r.serviceType)}</h1>
            <div className="text-sm text-[#9CA3AF] mt-1">{r.description || "No description"}</div>
          </div>
          <div className="text-right">
            <span className={`status-pill status-${r.status}`}>{STATUS_LABEL[r.status]}</span>
            <div className="font-display font-black text-xl mt-2">{formatZAR(r.price)}</div>
          </div>
        </div>

        {/* Status tracker */}
        {r.status !== "cancelled" && (
          <div className="border border-white/10 rounded-md p-4 mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display mb-3">Status</div>
            <div className="relative flex justify-between">
              <div className="absolute top-3 left-3 right-3 h-px bg-zinc-200" />
              <div className="absolute top-3 left-3 h-px bg-[#3CFF7E]" style={{ width: `${(currentIdx / (STATUS_ORDER.length - 1)) * 100}%`, maxWidth: "calc(100% - 24px)" }} />
              {STATUS_ORDER.map((s, i) => (
                <div key={s} className="relative flex flex-col items-center flex-1">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center z-10 ${
                    i <= currentIdx ? "bg-[#3CFF7E] border-[#3CFF7E]" : "bg-[#111827] border-white/20"
                  }`}>
                    {i <= currentIdx && <CheckCircle size={12} className="text-white" />}
                  </div>
                  <div className="text-[9px] font-bold uppercase tracking-wider mt-1 font-display text-center">
                    {STATUS_LABEL[s]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Parties */}
        <div className="space-y-2 mb-4">
          {r.scheduledDate && (
            <div className="border border-[#E6FF3C]/40 bg-[#E6FF3C]/5 rounded-md p-3" data-testid="scheduled-badge">
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#E6FF3C] font-display">Scheduled For</div>
              <div className="font-display font-bold text-sm mt-0.5 text-[#E6FF3C]">
                {new Date(r.scheduledDate).toLocaleString("en-ZA", { dateStyle: "full", timeStyle: "short" })}
              </div>
            </div>
          )}
          <InfoRow label="Client" value={r.clientName} phone={r.clientPhone} phonesVisible={r.phonesVisible} />
          {r.mechanicName && <InfoRow label="Mechanic" value={r.mechanicName} phone={r.mechanicPhone} phonesVisible={r.phonesVisible} />}
          <InfoRow label="Location" value={r.location?.address || `${r.location?.lat}, ${r.location?.lng}`} />
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {r.assignedMechanicId && (
            <>
              <Link to={`/app/chat/${r.id}`} className="h-12 border border-white rounded-md flex items-center justify-center gap-2 font-display font-bold text-sm uppercase tracking-wider" data-testid="chat-btn">
                <MessageCircle size={16} /> Chat
              </Link>
              <a
                href={`https://www.openstreetmap.org/?mlat=${r.location?.lat}&mlon=${r.location?.lng}#map=17/${r.location?.lat}/${r.location?.lng}`}
                target="_blank" rel="noreferrer"
                className="h-12 border border-white rounded-md flex items-center justify-center gap-2 font-display font-bold text-sm uppercase tracking-wider"
                data-testid="navigate-btn"
              >
                <MapPin size={16} /> Navigate
              </a>
            </>
          )}
        </div>

        {/* Role-based controls */}
        {canAccept && (
          <button onClick={accept} data-testid="accept-job-btn" className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md mb-3">
            Accept This Job
          </button>
        )}

        {isAssigned && r.status === "accepted" && (
          <button onClick={() => updateStatus("arrived")} data-testid="arrived-btn" className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md mb-3">
            I've Arrived (Reveal Phone)
          </button>
        )}

        {isAssigned && r.status === "arrived" && (
          <button onClick={() => updateStatus("in_progress")} data-testid="start-job-btn" className="w-full h-14 bg-[#0B1E5B] text-white font-display font-bold uppercase tracking-wider rounded-md mb-3">
            Start Job
          </button>
        )}

        {isAssigned && r.status === "in_progress" && (
          <button onClick={() => updateStatus("completed")} data-testid="complete-job-btn" className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md mb-3">
            Mark Completed
          </button>
        )}

        {(isClient || isAssigned) && ["pending", "accepted"].includes(r.status) && (() => {
          let canCancel = true;
          let blockMsg = "";
          if (r.scheduledDate) {
            const hoursUntil = (new Date(r.scheduledDate).getTime() - Date.now()) / 36e5;
            if (hoursUntil < 24) {
              canCancel = false;
              blockMsg = "You can only cancel at least 24 hours before the scheduled service";
            }
          }
          return (
            <div>
              <button
                onClick={() => updateStatus("cancelled")}
                disabled={!canCancel}
                data-testid="cancel-btn"
                className={`w-full h-12 border font-display font-bold uppercase tracking-wider rounded-md ${
                  canCancel ? "border-[#F87171] text-[#F87171]" : "border-white/10 text-[#9CA3AF] cursor-not-allowed"
                }`}
              >
                Cancel Request
              </button>
              {!canCancel && (
                <div className="mt-2 text-xs text-[#9CA3AF]" data-testid="cancel-block-msg">{blockMsg}</div>
              )}
            </div>
          );
        })()}

        {/* Share job via WhatsApp — client only, while still pending */}
        {isClient && r.status === "pending" && (() => {
          const loc = r.location?.address || (r.location?.lat ? `${r.location.lat.toFixed(4)}, ${r.location.lng.toFixed(4)}` : "location pending");
          const msg =
            `🚨 New job available. Open the app now to accept.\n\n` +
            `🔧 Service: ${serviceLabel(r.serviceType)}\n` +
            `📍 Location: ${loc}\n` +
            `💰 Price: ${formatZAR(r.price)}`;
          const href = `https://wa.me/?text=${encodeURIComponent(msg)}`;
          return (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              data-testid="notify-whatsapp-btn"
              className="mt-3 w-full h-14 bg-[#25D366] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <Share2 size={18} /> Notify Mechanic on WhatsApp
            </a>
          );
        })()}

        {/* Payment — client only, completed & unpaid */}
        {isClient && r.status === "completed" && !r.paid && (
          <div className="border border-white/10 rounded-md p-4 mb-3 bg-[#111827]" data-testid="payment-panel">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display mb-3">
              Choose payment method
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <PayOption active={payMethod === "card"} onClick={() => setPayMethod("card")} icon={<CreditCard size={18} />} label="Card" testid="paymethod-card" />
              <PayOption active={payMethod === "eft"} onClick={() => setPayMethod("eft")} icon={<Building2 size={18} />} label="EFT" testid="paymethod-eft" />
              <PayOption active={payMethod === "cash"} onClick={() => setPayMethod("cash")} icon={<Banknote size={18} />} label="Cash" testid="paymethod-cash" />
            </div>

            {payMethod === "card" && (
              <button
                onClick={payNow}
                disabled={paying}
                data-testid="pay-now-btn"
                className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60"
              >
                <CreditCard size={18} /> {paying ? "Redirecting..." : `Pay ${formatZAR(r.price)} with Card`}
              </button>
            )}

            {payMethod === "eft" && (
              <div data-testid="eft-panel">
                <div className="border border-white/10 rounded-md p-3 mb-3 bg-[#0A0A0A] space-y-1 text-sm">
                  <EftRow label="Bank" value="Standard Bank" />
                  <EftRow label="Account Name" value="AutoFix Rescue (Pty) Ltd" />
                  <EftRow label="Account No." value="0000 0000 0000" />
                  <EftRow label="Branch" value="051001" />
                  <EftRow label="Reference" value={`AFR-${r.id.slice(0, 6).toUpperCase()}`} />
                  <EftRow label="Amount" value={formatZAR(r.price)} highlight />
                </div>
                <button
                  onClick={() => markPaid("eft")}
                  disabled={paying}
                  data-testid="eft-confirm-btn"
                  className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] disabled:opacity-60"
                >
                  {paying ? "Saving..." : "I Have Paid"}
                </button>
              </div>
            )}

            {payMethod === "cash" && (
              <div data-testid="cash-panel">
                <p className="text-sm text-[#9CA3AF] mb-3">
                  Confirm you handed <span className="text-white font-bold">{formatZAR(r.price)}</span> in cash to the mechanic.
                </p>
                <button
                  onClick={() => markPaid("cash")}
                  disabled={paying}
                  data-testid="cash-confirm-btn"
                  className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] disabled:opacity-60"
                >
                  {paying ? "Saving..." : "Mark as Paid (Cash)"}
                </button>
              </div>
            )}
          </div>
        )}

        {r.paid && (
          <div className="border-2 border-[#3CFF7E] rounded-md p-3 flex items-center gap-2 mb-3" data-testid="paid-badge">
            <CheckCircle size={18} className="text-[#3CFF7E]" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3CFF7E] font-display">PAID</div>
              <div className="text-sm text-white">
                {formatZAR(r.price)} · {r.payment_method ? r.payment_method.toUpperCase() : "STRIPE"}
              </div>
            </div>
          </div>
        )}

        {/* Rating */}
        {isClient && r.status === "completed" && !r.rated && (
          <div className="mt-6 border border-white/10 rounded-md p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display mb-2">Rate Mechanic</div>
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onClick={() => setRating(i)} data-testid={`rate-${i}`}>
                  <Star size={24} className={i <= rating ? "fill-[#3CFF7E] text-[#3CFF7E]" : "text-[#9CA3AF]"} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Optional comment"
              data-testid="rating-comment"
              className="w-full border border-white/20 rounded-md p-3 mb-2 focus:outline-none focus:border-[#3CFF7E]"
            />
            <button onClick={submitRating} data-testid="submit-rating-btn" className="w-full h-12 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md">
              Submit Rating
            </button>
          </div>
        )}

        {r.rated && (
          <div className="mt-4 border border-white/10 rounded-md p-3 flex items-center gap-2">
            <Star size={16} className="fill-[#3CFF7E] text-[#3CFF7E]" />
            <div className="text-sm">Rated {r.ratingValue}/5 {r.ratingComment ? `— "${r.ratingComment}"` : ""}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, phone, phonesVisible = true }) {
  // Only render a click-to-call link when the backend has explicitly flagged
  // the phones as visible (status === arrived / in_progress / completed, or
  // admin). Until then we show a chat-only hint so nothing leaks through
  // browser inspection — the phone string itself is empty from the API.
  const callable = phone && phonesVisible;
  return (
    <div className="border border-white/10 rounded-md p-3 flex items-center justify-between" data-testid={`info-${label.toLowerCase()}`}>
      <div>
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">{label}</div>
        <div className="font-display font-bold text-sm mt-0.5">{value}</div>
      </div>
      {callable ? (
        <a
          href={`tel:${phone}`}
          className="h-10 w-10 border border-white rounded-md flex items-center justify-center active:scale-[0.98]"
          data-testid={`call-${label.toLowerCase()}`}
          aria-label={`Call ${label}`}
        >
          <Phone size={16} />
        </a>
      ) : (
        (label === "Client" || label === "Mechanic") && (
          <span
            className="text-[9px] font-display font-bold uppercase tracking-[0.15em] text-[#9CA3AF] border border-white/10 rounded-md px-2 py-1"
            data-testid={`phone-hidden-${label.toLowerCase()}`}
            title="Phone is revealed once the mechanic marks ARRIVED"
          >
            Chat only
          </span>
        )
      )}
    </div>
  );
}

function PayOption({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`border rounded-md p-3 flex flex-col items-center justify-center gap-1 transition-all ${
        active
          ? "border-[#3CFF7E] bg-[#3CFF7E]/10 text-[#3CFF7E]"
          : "border-white/10 text-[#9CA3AF] hover:border-white/30"
      }`}
    >
      {icon}
      <span className="text-[10px] font-display font-bold uppercase tracking-wider">{label}</span>
    </button>
  );
}

function EftRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">{label}</span>
      <span className={`font-mono text-sm ${highlight ? "text-[#3CFF7E] font-bold" : "text-white"}`}>{value}</span>
    </div>
  );
}

